import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { DataSchema, Schema } from 'types';
import type { TableProps } from '../components/common/Table/Table.tsx';
import type { Row } from '@tanstack/react-table';
import { isTableCellEditable } from 'utils/table';
import type { ResolveDocumentEditing } from 'types/documentEditing';

interface TableManagementContextValue {
  resolveDocumentEditing?: ResolveDocumentEditing;
  hasEditableActions: boolean;
  shouldShowRowControls: boolean;
  numberOfVisibleTableControls: number;
  hasHeliosFunctions: boolean;
  hasInnerTable?: boolean;
  draggable?: boolean;
  isRowNew: (rowId: string) => boolean;
  hasRowEditableCells: (rowId: string) => boolean;
  isCellEditable: (rowId: string, cellId: string) => boolean;
  showCancelEditButton: (rowId: string) => boolean;
  showSaveButton: (rowId: string) => boolean;
}

const TableManagementContext = createContext<TableManagementContextValue | null>(null);

interface TableManagementProviderProps<T extends keyof P, P extends DataSchema> {
  resolveDocumentEditing?: ResolveDocumentEditing;
  isHeliosTable: boolean;
  children: ReactNode;
  rows: Row<Schema<T, P>>[];
  dataSchema: TableProps<T, P>['dataSchema'];
  rowControls: TableProps<T, P>['rowControls'];
  tableControls: TableProps<T, P>['tableControls'];
  heliosRowFunctionsControls?: TableProps<T, P>['heliosRowFunctionsControls'];
  heliosRowControls?: TableProps<T, P>['heliosRowControls'];
  editRowId?: string[] | null;
  isEditableColumnsAllowed?: boolean;
  isColumnEditableForRow?: TableProps<T, P>['isColumnEditableForRow'];
  hasInnerTable?: boolean;
  draggable?: boolean;
  disableRowCancelEdit?: boolean;
  disableRowSave?: boolean;
}

export const TableManagementProvider = <T extends keyof P, P extends DataSchema>({
  isHeliosTable,
  resolveDocumentEditing,
  children,
  rows,
  dataSchema,
  rowControls,
  tableControls,
  heliosRowFunctionsControls,
  heliosRowControls,
  editRowId,
  isEditableColumnsAllowed,
  isColumnEditableForRow,
  hasInnerTable,
  draggable,
  disableRowCancelEdit,
  disableRowSave,
}: TableManagementProviderProps<T, P>) => {
  const hasVisibleRowsControls = useMemo(() => {
    if (!heliosRowControls) return false;

    return rows.some((row) =>
      Object.values(heliosRowControls).some((control) => (typeof control.visible === 'function' ? control.visible(row.original) : control.visible))
    );
  }, [rows, heliosRowControls]);

  const contextValue = useMemo(() => {
    const numberOfVisibleControls = Object.values(rowControls ?? {}).filter((rowControl) => rowControl.visible).length;
    const numberOfVisibleTableControls = Object.values(tableControls ?? {}).filter((tableControl) => tableControl.visible).length;
    const hasHeliosFunctions = (heliosRowFunctionsControls ?? []).length > 0;

    const shouldShowRowControls = numberOfVisibleControls > 0 || numberOfVisibleTableControls > 0 || hasVisibleRowsControls;

    const isRowNew = (rowId: string): boolean => rowId?.startsWith('new') ?? false;

    const isEntireRowEditable = (rowId: string): boolean => {
      if (isHeliosTable) {
        return false;
      }

      return isRowEditable(rowId);
    };

    const isRowEditable = (rowId: string): boolean => Boolean(editRowId?.includes(rowId));

    const rowsByIdentifier = new Map<string, (typeof rows)[number]>();
    rows.forEach((item) => {
      if (!rowsByIdentifier.has(item.original.identifier)) rowsByIdentifier.set(item.original.identifier, item);
    });

    const isCellEditable = (rowId: string, cellId: string): boolean => {
      const schemaEditable = Boolean(dataSchema[cellId]?.isEditable);
      const row = schemaEditable && isColumnEditableForRow ? rowsByIdentifier.get(rowId) : undefined;
      const columnEditable = schemaEditable && (!isColumnEditableForRow || !row || (isColumnEditableForRow(row.original, cellId) ?? true));

      return isTableCellEditable(rowId, columnEditable, {
        isHeliosTable,
        isEditableColumnsAllowed,
        isRowInEditMode: isRowEditable(rowId),
        isColumnDisabled: dataSchema[cellId]?.isEditable === false,
      });
    };

    const hasRowEditableCells = (rowId: string) => {
      const row = rowsByIdentifier.get(rowId);

      if (!row) return false;

      const hasEditableCells = row.getVisibleCells().some((cell) => isCellEditable(row.original.identifier, cell.column.id));

      if (isHeliosTable) {
        return hasEditableCells;
      }

      return hasEditableCells || isRowEditable(row.original.identifier);
    };

    const showCancelEditButton = (rowId: string) => (!disableRowCancelEdit && isEntireRowEditable(rowId)) || isRowNew(rowId);
    const showSaveButton = (rowId: string) => !disableRowSave && (isEntireRowEditable(rowId) || isRowNew(rowId));

    const hasEditableActions = rows.some((row) => showCancelEditButton(row.original.identifier) || showSaveButton(row.original.identifier));

    return {
      resolveDocumentEditing,
      hasEditableActions,
      shouldShowRowControls,
      numberOfVisibleTableControls,
      hasHeliosFunctions,
      hasInnerTable,
      draggable,
      isRowNew,
      isCellEditable,
      showCancelEditButton,
      showSaveButton,
      hasRowEditableCells,
    };
  }, [
    rowControls,
    tableControls,
    heliosRowFunctionsControls,
    hasVisibleRowsControls,
    rows,
    disableRowCancelEdit,
    disableRowSave,
    hasInnerTable,
    draggable,
    isHeliosTable,
    editRowId,
    isEditableColumnsAllowed,
    isColumnEditableForRow,
    dataSchema,
    resolveDocumentEditing,
  ]);

  return <TableManagementContext.Provider value={contextValue}>{children}</TableManagementContext.Provider>;
};

export function useTableManagement() {
  const context = useContext(TableManagementContext);
  if (!context) {
    throw new Error('useTableManagement must be used within a TableManagementProvider');
  }
  return context as TableManagementContextValue;
}
